import { Avatar, Badge } from 'flowbite-react';
import React, { useCallback, useEffect, useState } from 'react';
import { Img } from 'react-image';
import { formatCareerPathType, generatePositionTitle, revertPositionTitle } from 'shared-utils';
import { generateCompanyLogoAvatarSrc } from 'src/utils/companyLogo';
import {
	ApplicationStatus,
	PositionTitle,
	UIExtendedApplication,
	UserCareerPathType,
} from 'types';
type ApplicationWithBadge = UIExtendedApplication & {
	badgeTextForJobseeker?: string | null;
};

interface Props {
	application: ApplicationWithBadge;
}
const ApplicationCardHeader = ({ application }: Props) => {
	const positionTitle = application.isDriveApplication
		? formatCareerPathType(application.position?.title as UserCareerPathType)
		: formatCareerPathType(revertPositionTitle(application.position?.title as PositionTitle) as UserCareerPathType);
	const companyName = application.position?.company?.name;
	const companyUrl = application.position?.company?.url || '';
	const companyLogo = application.position?.company?.logo || '';

	// Local fallback in case backend doesn't send badgeTextForJobseeker
	const computeJobseekerBadge = (types?: ('auto' | 'manual')[] | null) => {
		if (!types || types.length === 0) return null;
		const hasAuto = types.includes('auto');
		const hasManual = types.includes('manual');
		if (hasAuto && !hasManual) return null; // auto only => no badge
		if (hasManual && !hasAuto) return 'Recommended'; // manual only => show Recommended
		if (hasAuto && hasManual) return 'Recommended'; // both => show Recommended
		return null;
	};

	const badgeText = application?.badgeTextForJobseeker ?? computeJobseekerBadge(application?.recommendationTypes);

	const [avatarImg, setAvatarImg] = useState<string>('');
	const defaultCompanyAvatar = '/assets/company_logo.svg';
	const generateAvatar = useCallback(async () => {
		const res = await generateCompanyLogoAvatarSrc(companyLogo, companyUrl);
		setAvatarImg(res || defaultCompanyAvatar);
	}, [companyLogo, companyUrl]);

	useEffect(() => {
		generateAvatar();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [companyLogo, companyUrl]);

	return (
		<div className="flex gap-[8px] relative">
			<div>
				<Avatar
					bordered={avatarImg === defaultCompanyAvatar}
					alt="companyLogo"
					img={avatarImg || defaultCompanyAvatar}
					rounded
					size={'md'}
				/>
			</div>

			<div className="flex-1">
				<div className="flex items-center gap-2">
					<p className="dark:text-[#fff] text-black font-inter text-[18px] font-[600] leadng-[28px]">{positionTitle}</p>
				</div>
				<p className="dark:text-[#C7C9D9] text-[#555770] font-inter text-[14px] font-[400] leadng-[20px]">
					{companyName}{' '}
					<span
						onClick={e => {
							e.stopPropagation();
							window.open(companyUrl);
						}}
						className="mb-[2px] ml-[8px] inline-block cursor-pointer align-middle"
					>
						<Img loading="lazy" src="/assets/externalLinkIcon.svg" alt="externalLinkIcon" width="16" height="16" />
					</span>
				</p>
			</div>
		</div>
	);
};

export default ApplicationCardHeader;
